import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Thin top-of-page progress bar shown during route changes. Replaces the
 * full-screen white spinner so the previous page stays visible until the new
 * chunk has loaded — much smoother than a hard cut.
 *
 * Implementation: we drive a CSS transform from 0 → 90% on navigation start,
 * then snap to 100% and fade out once the new route mounts (location updates).
 */
const RouteProgress = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef<number[]>([]);
  const firstRender = useRef(true);

  // When the location changes, mark the navigation as complete.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // Clear any pending timers
    timers.current.forEach(window.clearTimeout);
    timers.current = [];

    setProgress(100);
    const t1 = window.setTimeout(() => setVisible(false), 220);
    const t2 = window.setTimeout(() => setProgress(0), 400);
    timers.current.push(t1, t2);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [location.pathname, navType]);

  // Listen for click navigations on internal links to start the bar early.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = (e.target as HTMLElement)?.closest("a");
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href) return;
      if (/^(https?:|mailto:|tel:|#)/i.test(href)) return;
      if (target.getAttribute("target") === "_blank") return;
      // Same path? skip.
      if (href.split(/[?#]/)[0] === window.location.pathname) return;

      setVisible(true);
      setProgress(15);
      const t1 = window.setTimeout(() => setProgress(45), 80);
      const t2 = window.setTimeout(() => setProgress(75), 250);
      const t3 = window.setTimeout(() => setProgress(88), 600);
      timers.current.push(t1, t2, t3);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 z-[200] h-[2px] pointer-events-none"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 200ms ease" }}
    >
      <div
        className="h-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]"
        style={{
          width: `${progress}%`,
          transition: progress === 0 ? "none" : "width 300ms ease",
        }}
      />
    </div>
  );
};

export default RouteProgress;
